<template>
  <div class="link-paginator">
    <template
      v-for="(page, index) in pages"
      :key="index"
    >
      <PaginationLink
        v-if="displayPage(page)"
        :active="page === currentPage"
        :emit-only-click="emitOnlyClick"
        :page="page"
        :url="url(page)"
        :aria-label-prefix="ariaLabelPrefix"
        :current-page-aria-label="currentPageAriaLabel"
        @click="emitClick"
      />
      <span
        v-else-if="displayEllipsis(page)"
        class="link-paginator__ellipsis"
      >
        …
      </span>
    </template>
  </div>
</template>

<script setup lang="ts">
import { range } from 'lodash-es';

type LinkPaginatorProps = {
  currentPage: number;
  totalPages: number;
  url: (page: number) => string;
  emitOnlyClick?: boolean;
  ariaLabelPrefix?: string;
  currentPageAriaLabel?: string;
};

const props = withDefaults(defineProps<LinkPaginatorProps>(), {
  emitOnlyClick: false,
  ariaLabelPrefix: '',
  currentPageAriaLabel: '',
});

const emit = defineEmits<{ click: [target: string | number | undefined] }>();

const pages = computed(() => range(1, props.totalPages + 1));

function displayPage(page: number) {
  if (page === 1 || page === props.totalPages) return true;
  return (
    page === props.currentPage || page === props.currentPage - 1 || page === props.currentPage + 1
  );
}

function displayEllipsis(page: number) {
  if (page !== 2 && page !== props.totalPages - 1) return false;
  return !displayPage(page);
}

function emitClick(target: string | number | undefined) {
  emit('click', target);
}
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.link-paginator {
  display: flex;
  align-items: center;
  grid-gap: $grid-base * 2;
}
</style>
